
import { Button } from "@/components/ui/button";
import { ArrowLeft, Edit, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { parseISO, format } from "date-fns";

interface ProfileHeaderProps {
  name: string;
  age: number;
  birthday?: string;
  profilePictureUrl?: string;
  onEdit: () => void;
  onDelete: () => void;
}

export const ProfileHeader = ({ 
  name, 
  age, 
  birthday,
  profilePictureUrl, 
  onEdit, 
  onDelete 
}: ProfileHeaderProps) => {
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleDelete = () => {
    setShowDeleteDialog(false);
    onDelete();
  };

  const formatAge = (age: number) => {
    if (age === 0) return "0-11 months";
    return age === 1 ? "1 year old" : `${age} years old`;
  };

  const formatBirthday = (birthday?: string) => {
    if (!birthday) return "";
    // Parse the ISO date string and format it
    const date = parseISO(birthday);
    return format(date, "MMMM d, yyyy");
  };

  return (
    <div className="mb-6 space-y-4">
      <Button
        variant="ghost"
        onClick={() => navigate("/dashboard")}
        className="mb-2"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Dashboard
      </Button>
      
      <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16">
            <AvatarImage src={profilePictureUrl} alt={name} />
            <AvatarFallback>{name[0]}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900">
              {name}'s Profile
            </h1>
            <p className="text-gray-600 mt-1">
              {formatAge(age)} • {formatBirthday(birthday)}
            </p>
          </div>
        </div>
        <div className="flex flex-col xs:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onEdit} 
            className="w-full xs:w-auto"
          >
            <Edit className="mr-2 h-4 w-4" />
            Edit Profile
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => setShowDeleteDialog(true)}
            className="w-full xs:w-auto"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Profile
          </Button>
        </div>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Profile</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-left">
              <p>Are you sure you want to delete {name}'s profile? This action cannot be undone and will permanently delete:</p>
              <ul className="list-disc pl-6">
                <li>All personal information and profile settings</li>
                <li>Complete test history and results</li>
                <li>Progress tracking and achievements</li>
                <li>All associated learning data</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Profile
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
